import React, { SyntheticEvent } from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';

import InspectionModal from 'components/modals/InspectionModal';
import InspectionTemplate from 'models/InspectionTemplateModel';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import { Plus, Info, Trash2, Edit, Star } from 'lucide-react';

interface Props {}

interface State {
    name: string | null;
    boardOrderIndex: number;
    inspectionTemplates: InspectionTemplate[];

    isCreateInspectionModalOpen: boolean;
    isEditInspectionModalOpen: boolean;
    editableInpection?: InspectionTemplate;
}

class InspectionSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            name: null,
            boardOrderIndex: 0,
            inspectionTemplates: [],

            isCreateInspectionModalOpen: false,
            isEditInspectionModalOpen: false,
        };

        this.getValue = this.getValue.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.modalClosed = this.modalClosed.bind(this);
    }

    componentDidMount() {
        this.getInspectionTemplates();
    }

    getInspectionTemplates() {
        axios
            .get(`${config.apiUrl}/inspection/templates`)
            .then((res) => this.setState({ inspectionTemplates: res.data }))
            .catch((_) => console.log(_));
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state?.companySettings[name] || '';
    }

    handleChange(e: SyntheticEvent) {
        // @ts-ignore
        const { name, value } = e.target;
        // @ts-ignore
        this.setState({ [name]: value });
    }

    modalClosed(inspectionCreated?: boolean, inspectionUpdated?: boolean) {
        this.setState({
            isCreateInspectionModalOpen: false,
            isEditInspectionModalOpen: false,
            editableInpection: undefined,
        });
        this.componentDidMount();
    }

    handleDelete(inspectionTemplate: InspectionTemplate) {
        displayConfirmationModal(`inspection template ${inspectionTemplate.name} `)
            .then(() => {
                axios
                    .delete(`${config.apiUrl}/inspection/templates/${inspectionTemplate.id}`)
                    .then((_) => this.componentDidMount());
            })
            .catch((_) => _);
    }

    render() {
        // prettier-ignore
        const { isCreateInspectionModalOpen, isEditInspectionModalOpen, editableInpection, inspectionTemplates } =
            this.state;
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Inspection templates</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Inspection templates</h2>
                            <p className="text-gray-600 mt-1">
                                Design standardized inspection templates for seamless integration into estimates.
                            </p>
                        </div>
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            onClick={() =>
                                this.setState({
                                    isCreateInspectionModalOpen: true,
                                })
                            }
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add New</span>
                        </button>
                    </div>
                </div>
                {isCreateInspectionModalOpen && <InspectionModal onClose={this.modalClosed} />}
                {isEditInspectionModalOpen && (
                    <InspectionModal onClose={this.modalClosed} inspection={editableInpection} />
                )}{' '}
                <div className="flex-1 p-8">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-500 text-white">
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Title
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Groups
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Tasks
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Default
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {inspectionTemplates.map((template, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {template.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {template.groups?.length || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {template.groups?.reduce((acc, group) => acc + group.tasks.length, 0) ||
                                                    0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {template.isDefault && (
                                                    <div className="flex items-center space-x-1">
                                                        <Star className="h-4 w-4 text-amber-500 fill-current" />
                                                        <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                            Default
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() => {
                                                            this.setState({
                                                                editableInpection: template,
                                                                isEditInspectionModalOpen: true,
                                                            });
                                                        }}
                                                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => this.handleDelete(template)}
                                                        className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/* Empty State */}
                    {inspectionTemplates?.length < 1 && !isCreateInspectionModalOpen && (
                        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <Plus className="h-12 w-12 mx-auto" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No inspection templates yet</h3>
                            <p className="text-gray-600 mb-6">
                                Create your first inspection template to get started with standardized inspections.
                            </p>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors mx-auto"
                                onClick={() =>
                                    this.setState({
                                        isCreateInspectionModalOpen: true,
                                    })
                                }
                            >
                                <Plus className="h-4 w-4" />
                                <span>Create Template</span>{' '}
                            </button>
                        </div>
                    )}{' '}
                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How inspection templates work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Inspection templates define standardized checklists for vehicle inspections
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Templates are organized into groups, with each group containing specific tasks
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        The default inspection templates are automatically added to new Repair Orders
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Templates ensure consistency and thoroughness across all inspections
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Completed inspections can be seamlessly integrated into repair estimates
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default InspectionSettings;
